import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { CustomToastMessage, PathInfo, Role } from "types/general.types"
import { OWNER } from "constants/index"
import { DIALOG_KEYS } from "constants/dialogKeys"

export type DialogKey = typeof DIALOG_KEYS[keyof typeof DIALOG_KEYS]

type GlobalSlice = {
  message: CustomToastMessage | null;
  currentPageInfo: PathInfo;
  previousPageInfo: PathInfo;
  previousPath: string | null;
  currentPath: string | null;
  searchedUserIsLoggedInUser: boolean | null;
  role: Role;
  showEnlargedAvatar: boolean;
  showShareProfileModal: boolean;
  globalDialog: {
    activeDialog: DialogKey | null;
    dialogQueue: DialogKey[];
  }
}

const initialState = {
  message: null,
  currentPageInfo: {} as PathInfo,
  previousPageInfo: {} as PathInfo,
  previousPath: null,
  currentPath: null,
  searchedUserIsLoggedInUser: null,
  role: OWNER as Role,
  showEnlargedAvatar: false,
  showShareProfileModal: false,
  globalDialog: {
    activeDialog: null,
    dialogQueue: []
  }
} as GlobalSlice

export const globalSlice = createSlice({
  name: "globalSlice",
  initialState: initialState,
  reducers: {
    updateGlobalToastMessage: (state, action: PayloadAction<CustomToastMessage | null>) => {
      state.message = structuredClone(action.payload)
    },
    updateCurrentPageInfo: (state, action: PayloadAction<PathInfo | { [key: string]: any }>) => {
      state.currentPageInfo = {
        ...state.currentPageInfo,
        ...action.payload
      }
    },
    updatePreviousPageInfo: (state, action: PayloadAction<PathInfo | { [key: string]: any }>) => {
      state.previousPageInfo = {
        ...state.previousPageInfo,
        ...action.payload
      }
    },
    updateCurrentPath: (state, action: PayloadAction<string | null>) => {
      state.currentPath = action.payload
    },
    updatePreviousPath: (state, action: PayloadAction<string | null>) => {
      state.previousPath = action.payload
    },
    updateSearchedUserIsLoggedInUser: (state, action: PayloadAction<boolean | null>) => {
      state.searchedUserIsLoggedInUser = action.payload
    },
    updateRole: (state, action: PayloadAction<Role>) => {
      state.role = action.payload
    },
    updateShowEnlargedAvatarModal: (state, action: PayloadAction<boolean>) => {
      state.showEnlargedAvatar = action.payload
    },
    updateShowShareProfileModal: (state, action: PayloadAction<boolean>) => {
      state.showShareProfileModal = action.payload
    },
    enqueueGlobalDialog: (state, action: PayloadAction<DialogKey>) => {
      state.globalDialog.dialogQueue = [...state.globalDialog.dialogQueue, action.payload]
      if (!state.globalDialog.activeDialog) {
        state.globalDialog.activeDialog = state.globalDialog.dialogQueue.shift()!
      }
    },
    dequeueGlobalDialog: (state) => {
      if (state.globalDialog.dialogQueue.length > 0) {
        state.globalDialog.activeDialog = state.globalDialog.dialogQueue.shift()!
      }
    },
    closeActiveDialog: (state) => {
      state.globalDialog.activeDialog = null
    },
    resetGlobalSlice: (state) => ({
      ...structuredClone(initialState),
      currentPageInfo: state.currentPageInfo,
      previousPageInfo: state.previousPageInfo,
      currentPath: state.currentPath,
      previousPath: state.previousPath
    })
  }
})

export const {
  updateGlobalToastMessage,
  updateCurrentPageInfo,
  updatePreviousPageInfo,
  updateCurrentPath,
  updatePreviousPath,
  updateSearchedUserIsLoggedInUser,
  updateRole,
  updateShowEnlargedAvatarModal,
  updateShowShareProfileModal,
  enqueueGlobalDialog,
  dequeueGlobalDialog,
  closeActiveDialog,
  resetGlobalSlice
} = globalSlice.actions

export default globalSlice.reducer

export const closeActiveDialogWithDelay = createAsyncThunk(
  "global/closeActiveDialogWithDelay",
  async (_, { dispatch }) => {
    dispatch(closeActiveDialog())
    await new Promise((resolve) => setTimeout(resolve, 1000))
    dispatch(dequeueGlobalDialog())
  }
)
